import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal, NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Subject, forkJoin } from "rxjs";
import { ServiceRequestResponseTypeUtil } from "src/app/shared/utils/service-request-response.utils";
import { GeneralHistoryModel, HistoryClient, RegisterSignalClient, RegisteredSignal, RequestorSignalClient, ServiceRequestResponseTypes, SignalRequestor, SignalStatus, SignalStatusClient, TipAvaria, TipAvariaClient, UserProfile, WorkingGroupClient, WorkingGroups } from "src/app/web-api-client";
import { WtStorageService } from "src/app/wt-storage.service";
import { UserProfessionTypes } from "../../../enums/user-professions.enum";
import { DateTimeUtils } from "src/app/shared/utils/date-time.utils";
import { WorkingGroupTypes } from "../../../enums/working-groups.enum";
import { SignalStatusTypes } from "../../../enums/signal-status.enum";
import { SearchCountryField, CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input-gg';
import { PhoneNumberUtils } from "src/app/shared/utils/phone-number.utils";
import { ToastrService } from "ngx-toastr";

/**
 * Тhis component is rendered inside a modal and is used both for adding
 * and editing a signal request.
 *
 * Based on the user role the UI form fields may vary.
 */

@Component({
  selector: 'app-add-signal-registration',
  templateUrl: './add-signalRegistration.component.html',
  styleUrls: ['./add-signalRegistration.component.scss']
})
export class AddSignalRegistrationComponent implements OnInit {
  /**
   * If this field is populated it means that the modal is used in edit mode.
   * Otherwise the modal is in ADD mode.
   */
  @Input() registeredSignal: RegisteredSignal = null;

  public onClose = new Subject<void>();
  public onSignalAdded = new Subject<RegisteredSignal>();
  public onSignalUpdated = new Subject<RegisteredSignal>();

  public userProfile: UserProfile;

  public avariaTypesList: TipAvaria[] = [];
  public wGroups: WorkingGroups[] = [];

  public form: FormGroup;
  public phoneFieldEnabled = true;
  public loading = false;

  public signalStatusTypes = SignalStatusTypes;
  public formPhysicalCheckBtnName = 'signalPhysicalCheck';
  public formRegisterIncidentBtnName = 'registerIncident';
  public formNoIncidentBtnName = 'noIncident';

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;

  public responseTypes = ServiceRequestResponseTypeUtil.formKeyValueList();
  private signalRequestorsList: SignalRequestor[] = [];
  private signalStatusesList: SignalStatus[] = [];
  public generalHistory: GeneralHistoryModel[] = [];


  files: File[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly signalStatusService: SignalStatusClient,
    private readonly tipAvariaService: TipAvariaClient,
    private readonly signalRequestorService: RequestorSignalClient,
    private readonly registerSignalService: RegisterSignalClient,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly workingGroupClient: WorkingGroupClient,
    private readonly localStorage: WtStorageService,
    public readonly activeModal: NgbActiveModal,
    private toastr: ToastrService, 
    private readonly historyClient:HistoryClient
  ) { }

  ngOnInit(): void {
    this.userProfile = this.localStorage.getuser();
    if (this.registeredSignal) {
      console.log(this.registeredSignal);
      this.normalizeTheRegisteredSignal();
    }

    this.loadNomenclatures();
  }

  get isEditMode(): boolean {
    return this.registeredSignal !== null;
  }

  get isWorkingGroupEditEnabled(): boolean {
    return this.isEditMode && this.isUserTechnicalManager;
  }

  public get isUserTechnicalManager(): boolean {
    return this.userProfile.userProfessionIdUserProfession === UserProfessionTypes.TECHNICAL_MANAGER;
  }

  public get isUserDispatcher(): boolean {
    return this.userProfile.userProfessionIdUserProfession === UserProfessionTypes.DISPATCHER;
  }

  public get isUserRepairGroupManager(): boolean {
    return this.userProfile.userProfessionIdUserProfession === UserProfessionTypes.REPAIR_GROUP_MANAGER;
  }

  public get isFalseSignal(): boolean {
    return this.registeredSignal && this.registeredSignal.signalStatusId == this.falseSignalStatus.id;
  }
  get positiveSignalStatus(): SignalStatus {
    return this.signalStatusesList.find(d => d.id == 1);
  }

  get falseSignalStatus(): SignalStatus {
    return this.signalStatusesList.find(d => d.id == 2);
  }

  get signalStatusCount(): number {
    return this.signalStatusesList.length;
  }

  get incidentLocation(): google.maps.LatLngLiteral | null {
    if (!this.registeredSignal) {
      return null;
    }

    return {
      lat: +this.registeredSignal.latitude,
      lng: +this.registeredSignal.longitude
    }
  }

  /**
   * Called upon pressing any of the from buttons which are of type "Submit"
   * The logic is handled based on the Button ID and/or the user role.
   *
   * @param $event the browser generated event.
   */
  public onSubmit($event: SubmitEvent) {
    const submitID = (<HTMLButtonElement>$event.submitter).name;
    if(!submitID) {
      return;
    }

    if (this.isUserDispatcher) {
      const signalStatusIsPositive = (+submitID === this.positiveSignalStatus.id);
      this.form.patchValue({
        positiveSignalStatus: signalStatusIsPositive,
        signalStatusId: signalStatusIsPositive ? SignalStatusTypes.ACCEPTED : SignalStatusTypes.FALSE_ALARM
      });
    } else if (this.isUserRepairGroupManager) {
      this.form.patchValue({
        signalStatusId: this.getSignalStatusWhenFormSubmitButtonClicked(submitID)
      });
    }

    this.saveSignal(submitID);
  }

  public onLocationUpdated($event: google.maps.LatLngLiteral) {
    this.form.patchValue({
      position: $event
    });
  }

  public onUserSearchClicked() {
    const phoneNumber = this.form.controls.phoneNumber.getRawValue() as PhoneNumber;
    if (!phoneNumber || this.form.controls.phoneNumber.invalid) {
      return;
    }

    this.phoneFieldEnabled = !this.phoneFieldEnabled;
    if (!this.phoneFieldEnabled) {
      const selectedRequestor = this.getSignalRequestorByPhoneNumber(phoneNumber.e164Number);
      console.log(selectedRequestor);

      if (selectedRequestor) {
        this.setSelectedSignalRequestorFormFieldNames(selectedRequestor);
        this.disableSignalInitiatorFormNamesFields();
      } else {
        this.enableSignalInitiatorFormNamesFields();
      }
      this.disableSignalInitiatorFormPhoneField();
    } else {
      this.enableAndResetSignalInitiatorFormPhoneField();
      this.disableAndResetSignalInitiatorFormNamesFields();
    }
  }

  public onFilesSelect(event: any) {
    this.files.push(...event.addedFiles);
  }

  public onFileRemove(event: any) {
    this.files.splice(this.files.indexOf(event), 1);
  }

  public getCyrilicRequestType(requestType: ServiceRequestResponseTypes): string {
    return ServiceRequestResponseTypeUtil.getCyrilicLabel(requestType);
  }



  public closeModal() {
    this.activeModal.close();
    this.onClose.next();
  }

  public getStatus() {
    return this.registeredSignal?.signalStatus.title;
  }


  public GetRemGroupaTitle(item: WorkingGroups): string {

    let manager = '';

    if (item.manager) {
      manager = ` (${item.manager.firstName} ${item.manager.fammilyName})`
    }

    return `${item.title}${manager}`;
  }

  //----------------------------------------------------------------------------------------------------------------------
  // PRVATE
  //----------------------------------------------------------------------------------------------------------------------
  private getSignalRequestorByPhoneNumber(phoneNumber: string): SignalRequestor | null {
    return this.signalRequestorsList.find(e => PhoneNumberUtils.comparePhoneNumbers(e.phoneNumber, phoneNumber)) || null;
  }

  private normalizeTheRegisteredSignal() {
    if (!this.registeredSignal) {
      return;
    }

    if (this.registeredSignal.checkoutWhen && DateTimeUtils.isDateCorrupted(this.registeredSignal.checkoutWhen)) {
      this.registeredSignal.checkoutWhen = null;
    }
  }

  private getSignalCloseEmployeeFromSubmitButtonClicked(submitID: string): number {

    let closeEmployeeId: number = null;
    if (submitID === this.formNoIncidentBtnName || submitID === this.formRegisterIncidentBtnName) {
      closeEmployeeId = this.userProfile.id;
    }

    return closeEmployeeId;
  }

  private getSignalCloseDateFromSubmitButtonClicked(submitID: string): Date {

    let closeDate: Date = null;
    if (submitID === this.formNoIncidentBtnName || submitID === this.formRegisterIncidentBtnName) {
      closeDate = new Date();
    }

    return closeDate;
  }

  private getSignalStatusWhenFormSubmitButtonClicked(submitID: string): SignalStatusTypes {
    let signalStatusID = SignalStatusTypes.ACCEPTED;

    if (submitID === this.formPhysicalCheckBtnName) {
      signalStatusID = SignalStatusTypes.CHECKING;
    } else if (submitID === this.formRegisterIncidentBtnName) {
      signalStatusID = SignalStatusTypes.CLOSED_ISSUE_IDENTIFIED;
    } else if (submitID === this.formNoIncidentBtnName) {
      signalStatusID = SignalStatusTypes.CLOSED_ISSUE_NOT_IDENTIFIED;
    }

    return signalStatusID;
  }

  /**
   * Initial form values based on weather we are adding or editting a signal.
   */
  private initializeForm() {
    const phoneNumber = this.registeredSignal?.signalRequestor?.phoneNumber || '';
    const firstName = this.registeredSignal?.signalRequestor?.name || 'Няма Въведен';
    const middleName = this.registeredSignal?.signalRequestor?.middleName || 'Няма Въведен';
    const lastName = this.registeredSignal?.signalRequestor?.fammily || 'Няма Въведен';
    const responseType = this.registeredSignal?.responseType || ServiceRequestResponseTypes.None;
    let title = this.registeredSignal?.title || '';
    const avariaTypeId = this.registeredSignal?.idTipAvaria || 0;
    const signalStatusId = this.registeredSignal?.signalStatusId || 0;
    const workGroupId = this.registeredSignal?.workingGroupsId || WorkingGroupTypes.NONE;
    const whenDate = DateTimeUtils.toFormLocalDateTime(this.registeredSignal?.when || null);
    const checkoutWhen = DateTimeUtils.toFormLocalDateTime(this.registeredSignal?.checkoutWhen || null);
    const description = this.registeredSignal?.description || '';
    const checkoutAdittionalInfo = this.registeredSignal?.checkoutAdittionalInfo || '';
    const position: google.maps.LatLngLiteral = this.registeredSignal != null
      ? { lat: +this.registeredSignal.latitude, lng: +this.registeredSignal.longitude }
      : null;

    let selectedAvariaTypeTitle = avariaTypeId? this.avariaTypesList.find(d => d.id == avariaTypeId).title : ' ';

    title = !title || title ==' '? selectedAvariaTypeTitle : title;

    this.form = this.fb.group({
      phoneNumber: [{ value: phoneNumber, disabled: this.isEditMode }, Validators.required],
      firstName: [{ value: firstName, disabled: true }],
      middleName: [{ value: middleName, disabled: true }],
      lastName: [{ value: lastName, disabled: true }],
      email: [{ value: 'nomail@mail.com', disabled: true }],
      responseType: [{ value: responseType, disabled: !this.isUserDispatcher }, Validators.required],
      title: [{ value: title, disabled: !this.isUserDispatcher }, Validators.required],
      avariaType: [{ value: avariaTypeId, disabled: !this.isUserDispatcher }, Validators.required],
      wGroup: [{ value: workGroupId, disabled: !this.isWorkingGroupEditEnabled }, Validators.required],
      whenDateTime: [{ value: whenDate, disabled: !this.isUserDispatcher }, Validators.required],
      description: new FormControl({ value: description, disabled: !this.isUserDispatcher }),
      position: [position, Validators.required],
      signalStatusId: [signalStatusId]
    });

    //If the user is Repair Group Manager we want to add two additinal form fields
    if (this.isUserRepairGroupManager) {
      const signalStatusID = this.registeredSignal.signalStatusId;

      this.form.addControl("checkoutWhen", new FormControl({ value: checkoutWhen, disabled: signalStatusID === SignalStatusTypes.CHECKING }, Validators.required));
      this.form.addControl("checkoutAdittionalInfo", new FormControl({ value: checkoutAdittionalInfo, disabled: signalStatusID === SignalStatusTypes.CHECKING }));

      if (signalStatusID !== SignalStatusTypes.CHECKING) {

      }
    }

    //We want to make sure that the technical manager can not submit the form
    //if no working group has been selected!
    if (workGroupId === WorkingGroupTypes.NONE && this.isUserTechnicalManager) {
      this.form.patchValue({
        wGroup: null,
      });
    }
  }

  private loadNomenclatures() {
    this.loading = true;

    var signalID = this.registeredSignal ? this.registeredSignal.id : 0;
    forkJoin([
      this.tipAvariaService.getList(),
      this.signalRequestorService.getList(),
      this.signalStatusService.getList(),
      this.workingGroupClient.getList(),
      this.historyClient.getVikSignalHistory(signalID)
    ]).subscribe(([avariaTypesList, signalRequestorsList, signalStatusesList, wGroups,historyItems]) => {
      this.avariaTypesList = avariaTypesList;
      this.signalRequestorsList = signalRequestorsList;
      this.signalStatusesList = signalStatusesList;
      this.wGroups = wGroups;

      this.generalHistory = historyItems.sort((a, b) => {
        // Parse the date strings to Date objects if they're not already Date objects
        const dateA = new Date(a.changesWhen);
        const dateB = new Date(b.changesWhen);

        // Return the difference in timestamps
        return dateB.getTime() - dateA.getTime();
      });


      this.initializeForm();


      this.loading = false;
      this.changeDetectorRef.detectChanges();
    });
  }

  private enableSignalInitiatorFormNamesFields() {
    this.form.controls.firstName.enable();
    this.form.controls.middleName.enable();
    this.form.controls.lastName.enable();
    this.form.controls.email.enable();
  }

  private disableSignalInitiatorFormNamesFields() {
    this.form.controls.firstName.disable();
    this.form.controls.middleName.disable();
    this.form.controls.lastName.disable();
    this.form.controls.email.disable();
  }

  private disableAndResetSignalInitiatorFormNamesFields() {
    this.form.patchValue({
      firstName: '',
      middleName: '',
      lastName: '',
      email: ''
    });

    this.disableSignalInitiatorFormNamesFields();
  }

  private enableAndResetSignalInitiatorFormPhoneField() {
    this.form.patchValue({
      phoneNumber: 0,
    });
    this.form.controls.phoneNumber.enable();
  }

  private disableSignalInitiatorFormPhoneField() {
    this.form.controls.phoneNumber.disable();
  }

  private setSelectedSignalRequestorFormFieldNames(signalRequestor: SignalRequestor) {
    if (signalRequestor) {
      this.form.patchValue({
        firstName: signalRequestor.name,
        middleName: signalRequestor.middleName,
        lastName: signalRequestor.fammily,
        email: signalRequestor.email,
      });
    }
  }

  private buildSignalRequestorFromFormData(): SignalRequestor {
    const formValues = this.form.getRawValue() as SignalRequestFormData;
    const phoneNumberObj = formValues.phoneNumber;

    const signalRequestor = new SignalRequestor();
    signalRequestor.phoneNumber = PhoneNumberUtils.removeNonDigitsFromPhoneNumber(phoneNumberObj.e164Number);

    const selectedRequestor = this.getSignalRequestorByPhoneNumber(phoneNumberObj.e164Number);
    if (selectedRequestor) {
      signalRequestor.id = selectedRequestor.id;
      signalRequestor.name = selectedRequestor.name.toString();
      signalRequestor.middleName = selectedRequestor.middleName.toString();
      signalRequestor.fammily = selectedRequestor.fammily.toString();
      signalRequestor.email = selectedRequestor.email.toString();
    }
    else {
      signalRequestor.id = 0;
      signalRequestor.name = formValues.firstName;
      signalRequestor.middleName = formValues.middleName;
      signalRequestor.fammily = formValues.lastName;
      signalRequestor.email = formValues.email;
    }

    return signalRequestor;
  }

  private buildRegisteredSignalFromFormData(): RegisteredSignal {
    const formValues = this.form.getRawValue() as SignalRequestFormData;
    console.log(formValues);

    let registeredSignal = new RegisteredSignal();
    if (this.registeredSignal) {
      registeredSignal = JSON.parse(JSON.stringify(this.registeredSignal));
    }

    registeredSignal.idTipAvaria = formValues.avariaType;
    registeredSignal.responseType = formValues.responseType;
    registeredSignal.title = formValues.title;
    registeredSignal.description = formValues.description;
    registeredSignal.latitude = formValues.position.lat.toString();
    registeredSignal.longitude = formValues.position.lng.toString();
    registeredSignal.when = new Date(formValues.whenDateTime);
    registeredSignal.workingGroupsId = formValues.wGroup;
    registeredSignal.signalStatusId = formValues.signalStatusId;
    registeredSignal.signalRequestor = this.buildSignalRequestorFromFormData();

    if (this.isUserRepairGroupManager) {
      if (formValues.signalStatusId === SignalStatusTypes.CHECKING) {
        registeredSignal.checkoutWhen = new Date(formValues.checkoutWhen);
        registeredSignal.checkoutAdittionalInfo = formValues.checkoutAdittionalInfo;
      }
    }

    return registeredSignal;
  }

  private saveSignal(submitId:string) {
    if (this.form.valid) {
      const registeredSignal = this.buildRegisteredSignalFromFormData();

      registeredSignal.closeDate = this.getSignalCloseDateFromSubmitButtonClicked(submitId);
      registeredSignal.closeEmployeeId = this.getSignalCloseEmployeeFromSubmitButtonClicked(submitId);

      if (!registeredSignal.title || registeredSignal.title==' ') {
        registeredSignal.title = this.avariaTypesList.find(d => d.id == registeredSignal.idTipAvaria).title;
      }

      if (!registeredSignal.checkoutWhen) {
        registeredSignal.checkoutWhen = new Date('1990-01-01');
      }

      if (this.isEditMode) {
        this.registerSignalService.update(registeredSignal).subscribe(res => {

          this.toastr.success("Успешно Редактиран", "Сигнал", {
            timeOut: 3000,
            closeButton: true,
            positionClass: "toast-top-right",
          });

          if (res) {
            this.onSignalUpdated.next(registeredSignal);
          }
          this.onClose.next();
          this.closeModal();
        });
      } else {
        this.registerSignalService.create(registeredSignal).subscribe(res => {

          this.toastr.success("Успешно Създаден", "Сигнал", {
            timeOut: 3000,
            closeButton: true,
            positionClass: "toast-top-right",
          });

          if (res) {
            this.onSignalAdded.next(null);
          }
          this.onClose.next();
          this.closeModal();
        });
      }
    }
  }
}

export interface SignalRequestFormData {
  firstName: string;
  middleName: string;
  lastName: string;
  phoneNumber: PhoneNumber;
  description: string;
  email?: string;
  avariaType: number;
  responseType: number;
  title: string;
  wGroup: number;
  whenDateTime: Date | string;
  checkoutWhen: Date | string;
  checkoutAdittionalInfo: string;
  position: google.maps.LatLngLiteral;
  positiveSignalStatus: boolean;
  signalStatusId: number;
}

export interface PhoneNumber {
  countryCode: string;
  dialCode: string;
  e164Number: string;
  internationalNumber: string;
  nationalNumber: string;
  number: string;
}
